<template>
  <div class="appointment-widget" data-test="appointment-widget">
    <div class="widget-header">
      <div class="widget-icon"><i class="fas fa-calendar-check"></i></div>
      <div class="widget-heading">
        <h3 class="widget-title">{{ t('website.home.widget.title') }}</h3>
        <p class="widget-subtitle">{{ t('website.home.widget.subtitle') }}</p>
      </div>
    </div>

    <div v-if="widgetLoading" class="widget-loading">
      <span class="spinner-dot"></span>
    </div>

    <template v-else>
      <div class="field">
        <label>{{ t('website.home.widget.professional') }}</label>
        <select
          v-if="professionals.length > 1"
          v-model="selectedProfessionalId"
          class="widget-select"
        >
          <option v-for="p in professionals" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <div v-else class="professional-single">
          <span class="professional-avatar"><i class="fas fa-user-md"></i></span>
          <span>{{ activeProfessional?.name || '' }}</span>
        </div>
      </div>

      <div v-if="services.length > 0" class="field">
        <label>{{ t('website.home.widget.specialty') }}</label>
        <div class="pill-row">
          <button
            v-for="s in services"
            :key="s.id"
            type="button"
            class="pill"
            :class="{ active: selectedSpecialtyId === s.id }"
            @click="selectService(s.id)"
          >
            {{ s.name }}
            <span v-if="s.duration_blocks" class="pill-duration"
              >· {{ durationLabel(s.duration_blocks) }}</span
            >
          </button>
        </div>
      </div>

      <div class="widget-body">
        <div v-if="slotsLoading" class="widget-loading inner">
          <span class="spinner-dot"></span>
        </div>

        <div v-else-if="loadErrorText" class="widget-error">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ loadErrorText }}</p>
          <button type="button" class="link-btn" @click="init">
            {{ t('website.home.widget.retry') }}
          </button>
        </div>

        <template v-else-if="totalSlots > 0">
          <div class="slots-heading">
            <p class="section-label">{{ t('website.home.widget.timeHeader') }}</p>
            <span v-if="timezoneLabel" class="timezone-pill">
              <i class="fas fa-globe"></i> {{ timezoneLabel }}
            </span>
          </div>

          <div v-if="localError" class="inline-error">
            <i class="fas fa-exclamation-circle"></i> {{ localError }}
          </div>

          <div class="day-pills">
            <button
              v-for="group in slotGroups"
              :key="group.date"
              type="button"
              class="day-pill"
              :class="{ active: activeDate === group.date }"
              @click="activeDate = group.date"
            >
              <span class="day-label">{{ group.label }}</span>
              <span class="day-count">{{ group.slots.length }}</span>
            </button>
          </div>

          <div class="time-pills">
            <button
              v-for="slot in activeGroupSlots"
              :key="slot.datetime"
              type="button"
              class="time-pill"
              :class="{ selected: isSelected(slot) }"
              @click="selectSlot(slot)"
            >
              {{ slot.time }}
            </button>
          </div>
        </template>

        <div v-else class="widget-empty">
          <i class="fas fa-calendar-times"></i>
          <p>{{ t('website.home.widget.noSlots') }}</p>
          <router-link :to="{ name: 'index' }" class="link-btn solid">
            {{ t('website.home.widget.noSlotsCta') }}
          </router-link>
        </div>
      </div>

      <form v-if="selectedSlot && !successMessage" class="widget-form" @submit.prevent="confirm">
        <div class="form-grid">
          <div class="form-field">
            <label for="widget-name">{{ t('website.home.widget.name') }}</label>
            <input
              id="widget-name"
              v-model="form.name"
              type="text"
              :placeholder="t('website.home.widget.namePlaceholder')"
              :disabled="submitting"
            />
          </div>

          <div class="form-field">
            <label for="widget-email">{{ t('website.home.widget.email') }}</label>
            <input
              id="widget-email"
              v-model="form.email"
              type="email"
              :placeholder="t('website.home.widget.emailPlaceholder')"
              :disabled="submitting"
            />
          </div>

          <div class="form-field">
            <label for="widget-phone">{{ t('website.home.widget.phone') }}</label>
            <div class="phone-input-wrap">
              <span v-if="showCountry" class="dial-code">{{ dialCode }}</span>
              <input
                id="widget-phone"
                v-model="form.phone"
                type="tel"
                :class="{ 'with-dial': showCountry }"
                :placeholder="t('website.home.widget.phonePlaceholder')"
                :disabled="submitting"
              />
            </div>
          </div>

          <div v-if="showCountry" class="form-field">
            <label for="widget-country">{{ t('website.home.widget.country') }}</label>
            <select
              id="widget-country"
              v-model="form.country"
              class="widget-select"
              :disabled="submitting"
            >
              <option v-for="c in countries" :key="c.code" :value="c.code">
                {{ c.flag }} {{ c.code }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="submitError" class="form-error">
          <i class="fas fa-exclamation-triangle"></i> {{ submitError }}
        </div>

        <button type="submit" class="widget-cta" :disabled="submitting">
          <span v-if="submitting" class="spinner-dot light"></span>
          <template v-else>
            <i class="fas fa-check-circle"></i> {{ t('website.home.widget.book') }}
          </template>
        </button>
        <p class="privacy-note">
          <i class="fas fa-lock"></i> {{ t('website.home.widget.secondary') }}
        </p>
      </form>

      <div v-if="successMessage" class="widget-success">
        <div class="success-icon"><i class="fas fa-check"></i></div>
        <h4>{{ t('website.home.widget.successTitle') }}</h4>
        <p>{{ t('website.home.widget.successText') }}</p>
        <button type="button" class="link-btn solid" @click="resetAfterBooking">
          {{ t('website.home.widget.bookAnother') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { http as axios } from '@/services/http.service';
import { useAvailabilityStore } from '@/stores/availability.store';
import { useAppointmentsStore } from '@/stores/appointments.store';
import { useSpecialtiesStore, type Specialty } from '@/stores/specialties.store';
import { normalizeTimezone } from '@/composables/useTimezone';

interface Slot {
  datetime: string;
  time: string;
  date: string;
}

interface Professional {
  id: number;
  name: string;
  location?: string | null;
  include_country?: boolean;
  specialties?: Array<{ id: number; name: string; duration_blocks?: number }>;
}

interface DayGroup {
  date: string;
  label: string;
  slots: Slot[];
}

const DAYS_AHEAD = 5;
const MAX_SLOTS = 60;

const props = defineProps<{
  professionalId?: number | null;
}>();

const { t, locale } = useI18n();
const availabilityStore = useAvailabilityStore();
const appointmentsStore = useAppointmentsStore();
const specialtiesStore = useSpecialtiesStore();

const widgetLoading = ref(true);
const slotsLoading = ref(false);
const loadErrorText = ref('');
const professionals = ref<Professional[]>([]);
const selectedProfessionalId = ref<number | null>(props.professionalId ?? null);
const selectedSpecialtyId = ref<number | null>(null);
const activeDate = ref('');
const selectedSlot = ref<Slot | null>(null);
const localError = ref('');
const submitError = ref('');
const submitting = ref(false);
const successMessage = ref(false);
let priming = false;

const form = ref({
  name: '',
  email: '',
  phone: '',
  country: 'CL',
});

const countries = [
  { code: 'CL', flag: '🇨🇱', dial: '+56' },
  { code: 'MX', flag: '🇲🇽', dial: '+52' },
  { code: 'AR', flag: '🇦🇷', dial: '+54' },
  { code: 'PE', flag: '🇵🇪', dial: '+51' },
  { code: 'US', flag: '🇺🇸', dial: '+1' },
  { code: 'ES', flag: '🇪🇸', dial: '+34' },
] as const;

const TZ_NAMES: Record<string, string> = {
  'America/Santiago': 'Chile',
  'America/Buenos_Aires': 'Argentina',
  'America/Lima': 'Perú',
  'America/Bogota': 'Colombia',
  'America/Caracas': 'Venezuela',
  'America/La_Paz': 'Bolivia',
  'America/Asuncion': 'Paraguay',
  'America/Montevideo': 'Uruguay',
  'America/Quito': 'Ecuador',
  'America/Mexico_City': 'México',
  'America/New_York': 'USA',
  'America/Chicago': 'USA',
  'America/Denver': 'USA',
  'America/Los_Angeles': 'USA',
  'America/Toronto': 'Canadá',
  'America/Vancouver': 'Canadá',
  'Europe/Madrid': 'España',
  'Europe/Paris': 'Francia',
  'Europe/Berlin': 'Alemania',
  'Europe/London': 'Reino Unido',
  'Europe/Rome': 'Italia',
};

const activeProfessional = computed<Professional | null>(
  () => professionals.value.find((p) => p.id === selectedProfessionalId.value) ?? null,
);

const services = computed<Array<{ id: number; name: string; duration_blocks: number }>>(() => {
  const prof = activeProfessional.value;
  if (!prof) return [];
  const storeMap = new Map<number, Specialty>(specialtiesStore.items.map((s) => [Number(s.id), s]));
  const profSpecs = prof.specialties ?? [];
  if (profSpecs.length === 0) {
    return specialtiesStore.items.map((s) => ({
      id: Number(s.id),
      name: s.name,
      duration_blocks: s.duration_blocks,
    }));
  }
  return profSpecs.map((sp) => ({
    id: Number(sp.id),
    name: storeMap.get(Number(sp.id))?.name ?? sp.name,
    duration_blocks: storeMap.get(Number(sp.id))?.duration_blocks ?? sp.duration_blocks ?? 1,
  }));
});

const selectedSpecialty = computed(
  () => services.value.find((s) => s.id === selectedSpecialtyId.value) ?? null,
);

const timezoneLabel = computed(() => {
  const tz = normalizeTimezone(activeProfessional.value?.location);
  return tz === 'UTC' ? '' : TZ_NAMES[tz] ?? tz;
});

const showCountry = computed(() => activeProfessional.value?.include_country === true);

const dialCode = computed(() => countries.find((c) => c.code === form.value.country)?.dial ?? '');

const pad2 = (n: number) => String(n).padStart(2, '0');
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const todayStr = toDateStr(new Date());
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const tomorrowStr = toDateStr(tomorrow);

const slotGroups = computed<DayGroup[]>(() => {
  const byDate = new Map<string, Slot[]>();
  for (const slot of availabilityStore.nextSlots) {
    const arr = byDate.get(slot.date) ?? [];
    arr.push(slot);
    byDate.set(slot.date, arr);
  }
  const lc = locale.value === 'es' ? 'es-ES' : 'en-US';
  return [...byDate.entries()].map(([date, slots]) => {
    let label: string;
    if (date === todayStr) label = t('website.home.widget.today');
    else if (date === tomorrowStr) label = t('website.home.widget.tomorrow');
    else {
      label = new Intl.DateTimeFormat(lc, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).format(new Date(`${date}T00:00:00`));
    }
    return { date, label, slots };
  });
});

const activeGroupSlots = computed<Slot[]>(
  () => slotGroups.value.find((g) => g.date === activeDate.value)?.slots ?? [],
);

const totalSlots = computed(() => availabilityStore.nextSlots.length);

const isSelected = (slot: Slot) => selectedSlot.value?.datetime === slot.datetime;

const selectSlot = (slot: Slot) => {
  selectedSlot.value = slot;
  localError.value = '';
  submitError.value = '';
};

const selectService = (id: number) => {
  selectedSpecialtyId.value = id;
};

const durationLabel = (blocks: number): string => {
  const minutes = blocks * 30;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const fetchProfessionals = async () => {
  if (props.professionalId) {
    const { data } = await axios.get(`/api/v1/professionals/${props.professionalId}`);
    professionals.value = [data as Professional];
    selectedProfessionalId.value = props.professionalId;
    return;
  }
  const { data } = await axios.get('/api/v1/professionals');
  professionals.value = Array.isArray(data) ? (data as Professional[]) : [];
  if (professionals.value.length > 0) {
    selectedProfessionalId.value = professionals.value[0].id;
  }
};

const primeSpecialty = () => {
  priming = true;
  try {
    const srvs = services.value;
    selectedSpecialtyId.value = srvs.length > 0 ? srvs[0].id : null;
  } finally {
    priming = false;
  }
};

const loadSlots = async () => {
  const pid = selectedProfessionalId.value;
  if (!pid) return;
  slotsLoading.value = true;
  localError.value = '';
  selectedSlot.value = null;
  try {
    await availabilityStore.fetchNextSlots(
      pid,
      selectedSpecialtyId.value,
      MAX_SLOTS,
      null,
      DAYS_AHEAD,
    );
    loadErrorText.value = '';
    const firstWithSlots = slotGroups.value.find((g) => g.slots.length > 0);
    activeDate.value = firstWithSlots?.date ?? '';
  } catch {
    loadErrorText.value = t('website.home.widget.loadError');
  } finally {
    slotsLoading.value = false;
  }
};

const init = async () => {
  widgetLoading.value = true;
  loadErrorText.value = '';
  try {
    await specialtiesStore.fetchAll();
    await fetchProfessionals();
    if (selectedProfessionalId.value) {
      primeSpecialty();
      await loadSlots();
    }
  } catch {
    loadErrorText.value = t('website.home.widget.loadError');
  } finally {
    widgetLoading.value = false;
  }
};

watch(
  () => selectedProfessionalId.value,
  () => {
    if (!selectedProfessionalId.value) return;
    activeDate.value = '';
    selectedSlot.value = null;
    submitError.value = '';
    localError.value = '';
    primeSpecialty();
    void loadSlots();
  },
  { immediate: true },
);

watch(
  () => selectedSpecialtyId.value,
  () => {
    if (priming) return;
    if (selectedProfessionalId.value) void loadSlots();
  },
);

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const focusField = (id: string) => {
  setTimeout(() => document.getElementById(id)?.focus(), 10);
};

const confirm = async () => {
  submitError.value = '';
  if (!selectedSlot.value) return;
  if (services.value.length === 0 || !selectedSpecialtyId.value) {
    submitError.value = t('website.home.widget.requiredService');
    return;
  }
  if (!form.value.name.trim()) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldName'),
    });
    focusField('widget-name');
    return;
  }
  if (!form.value.email.trim()) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldEmail'),
    });
    focusField('widget-email');
    return;
  }
  if (!emailPattern.test(form.value.email.trim())) {
    submitError.value = t('website.home.widget.invalidEmail');
    focusField('widget-email');
    return;
  }
  if (!form.value.phone.trim()) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldPhone'),
    });
    focusField('widget-phone');
    return;
  }
  if (showCountry.value && !form.value.country) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldCountry'),
    });
    focusField('widget-country');
    return;
  }

  const prof = activeProfessional.value;
  if (!prof || !selectedSlot.value) return;

  submitting.value = true;
  try {
    await appointmentsStore.createOne({
      professional_id: prof.id,
      date_time: selectedSlot.value.datetime,
      requester_email: form.value.email.trim(),
      requester_name: form.value.name.trim(),
      requester_phone: showCountry.value
        ? `${dialCode.value} ${form.value.phone.trim()}`
        : form.value.phone.trim(),
      ...(showCountry.value ? { requester_country: form.value.country } : {}),
      specialty_id: selectedSpecialtyId.value ?? undefined,
    });
    availabilityStore.applySlotOccupied(prof.id, {
      datetime: selectedSlot.value.datetime,
      duration_blocks: selectedSpecialty.value?.duration_blocks ?? 1,
    });
    successMessage.value = true;
  } catch (err) {
    const message = extractError(err);
    if (isSlotTakenMessage(message)) {
      localError.value = message;
      availabilityStore.applySlotOccupied(prof.id, {
        datetime: selectedSlot.value.datetime,
        duration_blocks: selectedSpecialty.value?.duration_blocks ?? 1,
      });
      selectedSlot.value = null;
    } else {
      submitError.value = message;
    }
  } finally {
    submitting.value = false;
  }
};

const extractError = (err: unknown): string => {
  if (typeof err === 'string' && err !== 'Unknown Error') return err;
  if (err && typeof err === 'object') {
    const d = err as { error?: unknown; errors?: unknown };
    if (typeof d.error === 'string' && d.error) return d.error;
    if (Array.isArray(d.errors) && d.errors.length > 0) return String(d.errors[0]);
  }
  return t('website.home.widget.genericError');
};

const isSlotTakenMessage = (msg: string) =>
  /reservado|reserved|already been taken|no disponible|not available|no longer/i.test(msg);

const resetAfterBooking = () => {
  successMessage.value = false;
  form.value.name = '';
  form.value.email = '';
  form.value.phone = '';
  selectedSlot.value = null;
};

onMounted(init);
</script>

<style scoped>
.appointment-widget {
  background: white;
  border-radius: 24px;
  padding: clamp(1.25rem, 4vw, 2.25rem);
  box-shadow: 0 18px 50px rgba(141, 23, 196, 0.16);
  border: 1px solid rgba(141, 23, 196, 0.1);
  position: relative;
  overflow: hidden;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  text-align: left;
}

.appointment-widget::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, #8d17c4, #b24ff3, #8d17c4);
}

.widget-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
}

.widget-icon {
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  background: #f8f0ff;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #8d17c4;
}

.widget-title {
  margin: 0 0 3px;
  font-size: 1.15rem;
  font-weight: 900;
  color: #1a1a1a;
}

.widget-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.4;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 16px;
}

.field label,
.form-field label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.widget-select,
.form-field input {
  width: 100%;
  padding: 13px 16px;
  border: 2px solid #e9e2f4;
  border-radius: 14px;
  font-size: 0.95rem;
  color: #1e293b;
  background: white;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.widget-select:focus,
.form-field input:focus {
  outline: none;
  border-color: #8d17c4;
  box-shadow: 0 0 0 4px rgba(141, 23, 196, 0.08);
}

.professional-single {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  background: #f8f0ff;
  border-radius: 14px;
  font-weight: 700;
  color: #2d0066;
}

.professional-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8d17c4, #b24ff3);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pill {
  padding: 9px 16px;
  border-radius: 99px;
  border: 1.5px solid #e9e2f4;
  background: #fdfbff;
  font-size: 0.85rem;
  font-weight: 700;
  color: #8d17c4;
  cursor: pointer;
  transition: all 0.2s;
}

.pill:hover {
  border-color: #c97df5;
}

.pill-duration {
  font-weight: 600;
  opacity: 0.75;
  font-size: 0.78rem;
}

.pill.active .pill-duration {
  opacity: 0.9;
}

.pill.active {
  background: linear-gradient(135deg, #8d17c4, #7212a1);
  border-color: transparent;
  color: white;
}

.widget-body {
  margin-top: 20px;
}

.slots-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.section-label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 800;
  color: #2d0066;
}

.timezone-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #8d17c4;
  background: #f8f0ff;
  padding: 4px 10px;
  border-radius: 99px;
  white-space: nowrap;
}

.inline-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fee2e2;
  padding: 11px 14px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 14px;
}

.day-pills {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
  margin-bottom: 12px;
}

.day-pills::-webkit-scrollbar {
  display: none;
}

.day-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 9px 13px;
  border-radius: 13px;
  border: 1.5px solid #f0e6ff;
  background: #fdfbff;
  cursor: pointer;
  transition: all 0.25s;
  min-width: 62px;
  flex-shrink: 0;
}

.day-pill:hover {
  border-color: #c97df5;
}

.day-pill.active {
  background: linear-gradient(135deg, #8d17c4, #7212a1);
  border-color: transparent;
  color: white;
  box-shadow: 0 8px 20px rgba(141, 23, 196, 0.25);
}

.day-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #8d17c4;
  text-transform: capitalize;
}

.day-pill.active .day-label {
  color: white;
}

.day-count {
  font-size: 0.72rem;
  font-weight: 800;
  background: rgba(141, 23, 196, 0.12);
  color: #8d17c4;
  border-radius: 99px;
  padding: 1px 7px;
}

.day-pill.active .day-count {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.time-pills {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 9px;
}

.time-pill {
  padding: 13px 10px;
  border-radius: 13px;
  border: 1.5px solid rgba(141, 23, 196, 0.15);
  background: #fdfbff;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 800;
  color: #8d17c4;
  font-variant-numeric: tabular-nums;
  transition: all 0.25s;
}

.time-pill:hover {
  border-color: #8d17c4;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(141, 23, 196, 0.12);
}

.time-pill.selected {
  background: linear-gradient(135deg, #8d17c4, #7212a1);
  border-color: transparent;
  color: white;
  box-shadow: 0 10px 25px rgba(141, 23, 196, 0.28);
}

.widget-form {
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.phone-input-wrap {
  position: relative;
}

.dial-code {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 700;
  color: #64748b;
  font-size: 0.9rem;
  pointer-events: none;
}

.with-dial {
  padding-left: 56px !important;
}

.form-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fee2e2;
  padding: 11px 14px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.widget-cta {
  width: 100%;
  background: linear-gradient(135deg, #8d17c4, #710da1);
  color: white;
  border: none;
  padding: 16px;
  border-radius: 15px;
  font-size: 1.05rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 10px 30px rgba(141, 23, 196, 0.3);
  transition: all 0.3s;
}

.widget-cta:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(141, 23, 196, 0.4);
}

.widget-cta:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

.privacy-note {
  margin: 0;
  text-align: center;
  font-size: 0.75rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.widget-success {
  margin-top: 20px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.success-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.widget-success h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 900;
  color: #065f46;
}

.widget-success p {
  margin: 0 0 6px;
  font-size: 0.9rem;
  color: #047857;
  line-height: 1.5;
}

.widget-empty {
  text-align: center;
  padding: 30px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #8a7aa0;
}

.widget-empty i {
  font-size: 2rem;
  opacity: 0.5;
}

.widget-empty p {
  margin: 0;
  font-size: 0.9rem;
}

.widget-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
}

.widget-loading.inner {
  padding: 20px 0;
}

.spinner-dot {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(141, 23, 196, 0.2);
  border-top-color: #8d17c4;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

.spinner-dot.light {
  width: 22px;
  height: 22px;
  border-color: rgba(255, 255, 255, 0.3);
  border-top-color: white;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.widget-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 14px;
}

.widget-error i {
  font-size: 1.6rem;
}

.widget-error p {
  margin: 0;
  font-size: 0.9rem;
}

.link-btn {
  border: none;
  background: transparent;
  color: #8d17c4;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 99px;
}

.link-btn:hover {
  text-decoration: underline;
}

.link-btn.solid {
  background: #8d17c4;
  color: white;
  border-radius: 99px;
  padding: 10px 20px;
}

.link-btn.solid:hover {
  background: #7212a1;
  text-decoration: none;
}

@media (max-width: 640px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .appointment-widget {
    border-radius: 20px;
  }
}
</style>
